import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { addDays, subDays } from "date-fns";

import { Chart } from "@/components/chart";
import { useResource } from "@/hooks/resources";

type TimePlayedChartProps = {
  childId?: string | number;
  demo?: boolean;
};

const shortDateFormatter = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
});

const formatWeek = (iso: string) => {
  const start = new Date(iso + "T00:00:00");
  const end = addDays(start, 6);
  return `${shortDateFormatter.format(start)} – ${shortDateFormatter.format(end)}`;
};

export const TimePlayedChart = ({ childId, demo }: TimePlayedChartProps) => {
  const { t } = useTranslation("charts");

  const [timePlayedRaw] = useResource<Array<{ week: string, durationInMinutes: number, totalDurationInMinutes: number }>>(`/reports/time_played_for_all_songs?child_id=${childId}`)

  const timePlayed = useMemo(() =>
    (timePlayedRaw || []).map(entry => ({
      ...entry,
      week: formatWeek(entry.week),
    })),
    [timePlayedRaw]
  );

  let totalDurationInMinutes = 0;
  const demoDurationsInMinutes = [0, 4, 7, 0, 10, 8, 0, 9, 11, 0, 6, 3].map((durationInMinutes, index) => {
    const start = subDays(new Date(), 7 * (12 - index));
    return {
    week: `${shortDateFormatter.format(start)} – ${shortDateFormatter.format(addDays(start, 6))}`,
    durationInMinutes,
    totalDurationInMinutes: totalDurationInMinutes += durationInMinutes
  };})

  return (
    <Chart
      options={{
        tooltip: {
          trigger: "axis",
          valueFormatter: (value) => t("timePlayed.minutes", { count: Number(value) }),
        },
        xAxis: { name: "Week", type: "category" },
        yAxis: [{ name: "Minutes" }, { name: "Total Minutes" }],
        series: [{ type: "bar", name: "Weekly", yAxisIndex: 0 }, { type: "line", name: "All Time", yAxisIndex: 1, smooth: true }],
        dataset: {
          dimensions: ["week", "durationInMinutes", "totalDurationInMinutes"],
          source: demo ? demoDurationsInMinutes : (timePlayed || [])
        }
      }}
      className="h-[400px]"
    />
  )
}
